import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Ticket } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function AcceptInvite() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialCode = params.get("code") || "";
  const { acceptInvite } = useAuth();
  const [code, setCode] = useState(initialCode);
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialCode) verify(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (c) => {
    setChecking(true);
    setInfo(null);
    try {
      const { data } = await api.get(`/invites/verify/${encodeURIComponent(c)}`);
      setInfo(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await acceptInvite({ code, password });
      toast.success(t("auth.acceptinvite.welcome_to_the_team"));
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-lg bg-amber-500 text-white">
            <Ticket className="h-7 w-7" />
          </div>
          <div className="text-2xl font-bold text-foreground">{t("auth.acceptinvite.accept_invite")}</div>
          <div className="text-xs text-muted-foreground">{t("auth.acceptinvite.join_a_pump_as_staff")}</div>
        </div>
        <Card className="border-border">
          <CardHeader>
            <CardTitle>{t("auth.acceptinvite.enter_your_invite_code")}</CardTitle>
          </CardHeader>
          <CardContent>
            {!info ? (
              <form onSubmit={(e) => { e.preventDefault(); verify(code); }} className="space-y-4">
                <div>
                  <Label htmlFor="icode">{t("auth.acceptinvite.invite_code")}</Label>
                  <Input id="icode" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("auth.acceptinvite.inv_xxxxxxxx")} data-testid="invite-code" className="h-11 text-base font-mono md:h-9" required />
                </div>
                <Button type="submit" className="h-11 w-full md:h-10" disabled={checking} data-testid="invite-verify">
                  {checking ? "Checking..." : "Verify code"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="invite-accept-form">
                <div className="rounded-lg bg-amber-50 p-3 text-sm">
                  <div className="font-semibold text-foreground">Invited by {info.pump?.name || "the dealer"}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{t("auth.acceptinvite.name")} <b>{info.invite.name}</b></div>
                  <div className="text-xs text-muted-foreground">{t("auth.acceptinvite.phone")} <b>{info.invite.phone}</b></div>
                  <div className="text-xs text-muted-foreground">{t("auth.acceptinvite.role")} <b>{info.invite.role}</b></div>
                </div>
                <div>
                  <Label htmlFor="ipw">{t("auth.acceptinvite.choose_a_password_min_6")}</Label>
                  <Input id="ipw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required data-testid="invite-password" className="h-11 text-base md:h-9" />
                </div>
                <Button type="submit" className="h-11 w-full md:h-10" disabled={submitting} data-testid="invite-submit">
                  {submitting ? "Accepting..." : "Accept & sign in"}
                </Button>
                <button
                  type="button"
                  onClick={() => setInfo(null)}
                  className="w-full text-center text-xs text-muted-foreground hover:underline"
                >
                  Use a different code
                </button>
              </form>
            )}
            <div className="mt-4 text-center text-xs text-muted-foreground">
              <Link to="/login" className="font-semibold text-amber-600 hover:underline">{t("auth.acceptinvite.back_to_sign_in")}</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
